import {observer} from 'mobx-react-lite'
import React, {ReactElement, ReactNode, useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Hub} from 'yougile-model'
import {BgOpacityContainer} from '@components/common/bg-opacity-container/bg-opacity-container'
import {CoverControls} from '@components/file-viewer/file-controls/cover'
import {MoveToBody} from '@components/move-to-body'
import {useGlobalKeyDown} from '@containers/hooks'
import {FileViewData} from '@extensions/message-utils'
import {Task} from '@extensions/task'
import {Close, Next, Prev} from './file-controls/controls'
import {Info} from './file-controls/info'
import {ZoomControls} from './file-controls/zoom'
import {FilePreviewer} from './file-previewer/file-previewer'
import {FileView} from './file-view/file-view'
import {
  FileViewerContext,
  FileViewerStore,
  useFileViewerStore
} from './file-viewer-store'
import {Download} from './utils/download'

const TwMoveToBody = tw(MoveToBody)`
  top-0
`

interface Props {
  selectedIndex: number
  autoplay?: boolean
  files: FileViewData[]
  onClose: () => void
  onDownload?: () => void
  customControls?: React.ReactNode
  hub?: Hub
}

export const FileViewer = (props: Props) => {
  const {files, selectedIndex, onClose, autoplay, hub} = props
  const [store] = useState(() => new FileViewerStore())

  useEffect(() => {
    store.state.autoplay = autoplay ?? false
    store.setFiles(files)
    store.setCurrentFileIndex(selectedIndex !== -1 ? selectedIndex : 0)
    store.setOnClose(onClose)
    store.setHub(hub)
  }, [])

  return (
    <FileViewerContext.Provider value={store}>
      <FileViewerBase {...props} />
    </FileViewerContext.Provider>
  )
}

const Container = ({children}: {children: ReactNode}): ReactElement => {
  return (
    <BgOpacityContainer
      bgColorClassName="bg-main-old-dark"
      bgOpacityClassName="opacity-95"
      className="absolute w-screen h-screen z-2000"
    >
      {children}
    </BgOpacityContainer>
  )
}

export const FileViewerBase = observer((props: Props) => {
  const [state, store] = useFileViewerStore()

  const close = () => {
    state.onClose()
    return true
  }

  useGlobalKeyDown({
    escape: close,
    anyKey: ({keyCode}) => {
      if (keyCode === 37 /* left */) {
        store.prev()
      }
      if (keyCode === 39 /* right */) {
        store.next()
      }
    }
  })

  return (
    <TwMoveToBody>
      <Container>
        <Info />
        <Close />
        <Prev />
        <Next />
        <FileView />
        <FilePreviewer />
        <Controls {...props} />
      </Container>
    </TwMoveToBody>
  )
})

const Controls = observer(({customControls, onDownload}: Props) => {
  const [state] = useFileViewerStore()
  const isImage = state.currentFile?.contentType === 'image'
  return (
    <div className="absolute bottom-24 right-24">
      <div className="flex gap-32">
        {customControls}
        {isImage && <ZoomControls />}
        {isImage && state.hub && Task.isTask(state.hub) && <CoverControls />}
        <Download onDownload={onDownload} />
      </div>
    </div>
  )
})
